import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Divider,
  Grid,
  Paper,
  Typography,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { TextValidator } from "react-material-ui-form-validator";
import { useDispatch, useSelector } from "react-redux";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import { ValidatorForm } from "react-material-ui-form-validator";
import DateFnsUtils from "@date-io/date-fns";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import { DatePickerValidator } from "../../../../utilities/validators";
import {
  addPrescriptionRequest,
  getMedicineInventoryRequest,
  getPrescriptionDraftRequest,
  submitPrescriptionReportRequest,
  updatePrescriptionRequest,
} from "../../PrescriptionApiActions";
import { buildPrescriptionReportPayload } from "../../prescriptionReportPayload";
import { getUserInfo } from "../../../../utilities/storage";
import { showSnackbar } from "../../../../utilities/UtilitiesActions";
import { getOrgDoctorsRequest } from "../../../Doctors/DoctorApiActions";
import { getAssessmentComprehensiveReportRequest } from "../../../Athletes/PatientApiActions";
import Medicines from "./Medicines";
import Foods from "./Foods";
import SectionRows from "./SectionRows";
import { FOOD_TYPES } from "../../../../utilities/constants";
import { useStyles } from "./styles";

const defaultMedicine = {
  medicineName: "",
  dosage: "",
  frequency: "",
  duration: "",
  medicineTime: "",
  instruction: "",
};

const defaultComplaint = { complaint: "", duration: "" };

// Row shapes for the sections the report service auto-fills, per its contract:
// rootCauseFactors [{term, detail}], lifestylePlan [str], yogaAsanas [{asana, duration}].
const COMPLAINT_FIELDS = [
  { key: "complaint", label: "Complaint", sm: 8, placeholder: "E.g., Weight gain" },
  { key: "duration", label: "Duration", sm: 4, placeholder: "E.g., 2 years", requiredWith: "complaint" },
];

const ROOT_CAUSE_FIELDS = [
  { key: "term", label: "Factor", sm: 5, placeholder: "E.g., Viruddha Ahara" },
  { key: "detail", label: "Effect", sm: 7, multiline: true },
];

const LIFESTYLE_FIELDS = [{ key: "item", label: "Lifestyle Recommendation" }];

const YOGA_FIELDS = [
  { key: "asana", label: "Asana", sm: 8, placeholder: "E.g., Tadasana" },
  { key: "duration", label: "Duration", sm: 4, placeholder: "E.g., 1-2 minutes" },
];

// eat/avoid foods arrive as plain strings or { category, items[] } groups; flatten
// each into a single readable line per food row.
const flattenAssessmentFoods = (list) =>
  (Array.isArray(list) ? list : [])
    .map((food) => {
      if (typeof food === "string") return food.trim();
      if (food && Array.isArray(food.items)) {
        const items = food.items.join(", ");
        return food.category ? `${food.category}: ${items}` : items;
      }
      return "";
    })
    .filter(Boolean);

const PrescriptionForm = ({
  caseId,
  patient,
  externalReport,
  patientPhone,
  history,
  setIsGoBackClicked,
  isOpenPrescriptionForm,
  onClosePrescriptionForm,
  prescription,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const allMedicines = useSelector((state) =>
    (state.prescriptions.medicineInventory || []).filter(
      (med) => med.active === true
    )
  );

  const doctors = useSelector((state) => state.doctors?.doctors?.rows || []);

  useEffect(() => {
    dispatch(getMedicineInventoryRequest());
    dispatch(getOrgDoctorsRequest());
  }, [dispatch]);

  const [medicines, setMedicines] = useState({});

  const [foods, setFoods] = useState({
    foodToEat: [""],
    foodToAvoid: [""],
  });

  const [complaints, setComplaints] = useState([{ ...defaultComplaint }]);
  const [rootCauseFactors, setRootCauseFactors] = useState([
    { term: "", detail: "" },
  ]);
  const [lifestylePlan, setLifestylePlan] = useState([""]);
  const [yogaAsanas, setYogaAsanas] = useState([{ asana: "", duration: "" }]);
  const [nextFollowup, setNextFollowup] = useState(null);
  const [selectedDoctorId, setSelectedDoctorId] = useState("");
  const [doctorInput, setDoctorInput] = useState("");
  const [repeatTests, setRepeatTests] = useState("");
  const [note, setNote] = useState("");

  // Auto-prefill the four sections the report service sources (root cause,
  // nutrition, lifestyle, yoga) so the doctor reviews them before signing.
  // The draft is authoritative; the chatbot assessment only fills the food
  // lists it left empty. Chained rather than run in parallel, so the slower
  // request cannot overwrite the better answer.
  const prefilledRef = useRef(false);

  useEffect(() => {
    if (prescription || !patientPhone || prefilledRef.current) return;
    prefilledRef.current = true;

    dispatch(getPrescriptionDraftRequest(patientPhone))
      .catch(() => null)
      .then((draft) => {
        const eat = draft?.nutrition?.add || [];
        const avoid = draft?.nutrition?.avoid || [];
        if (draft?.rootCauseFactors?.length) {
          setRootCauseFactors(draft.rootCauseFactors);
        }
        if (draft?.lifestylePlan?.length) setLifestylePlan(draft.lifestylePlan);
        if (draft?.yogaAsanas?.length) setYogaAsanas(draft.yogaAsanas);
        if (eat.length || avoid.length) {
          setFoods((prev) => ({
            foodToEat: eat.length ? eat : prev.foodToEat,
            foodToAvoid: avoid.length ? avoid : prev.foodToAvoid,
          }));
        }
        if (eat.length && avoid.length) return null;
        return dispatch(
          getAssessmentComprehensiveReportRequest(patientPhone)
        ).catch(() => null);
      })
      .then((payload) => {
        if (!payload) return;
        const foodToEat = flattenAssessmentFoods(payload.eatFoods);
        const foodToAvoid = flattenAssessmentFoods(payload.avoidFoods);
        if (!foodToEat.length && !foodToAvoid.length) return;
        setFoods((prev) => {
          const eatEmpty = (prev.foodToEat || []).every((f) => !f);
          const avoidEmpty = (prev.foodToAvoid || []).every((f) => !f);
          return {
            foodToEat:
              eatEmpty && foodToEat.length ? foodToEat : prev.foodToEat,
            foodToAvoid:
              avoidEmpty && foodToAvoid.length ? foodToAvoid : prev.foodToAvoid,
          };
        });
      });
  }, [prescription, patientPhone, dispatch]);

  useEffect(() => {
    ValidatorForm.addValidationRule("requiredDoctor", (value) => {
      return selectedDoctorId && selectedDoctorId !== "";
    });
    return () => {
      ValidatorForm.removeValidationRule("requiredDoctor");
    };
  }, [selectedDoctorId]);

  useEffect(() => {
    if (prescription?.doctorId && doctors.length > 0) {
      const matchedDoctor = doctors.find(
        (doc) => doc.userId === prescription.doctorId
      );
      if (matchedDoctor) {
        const fullName = `${matchedDoctor.firstName || ""} ${
          matchedDoctor.lastName || ""
        }`.trim();
        setSelectedDoctorId(matchedDoctor.userId);
        setDoctorInput(fullName);
      }
    }
  }, [prescription, doctors]);

  useEffect(() => {
    if (prescription) {
      const groupedMedicines = {};

      prescription.Medicines?.forEach((med) => {
        const type = med.type || "meds";
        if (!groupedMedicines[type]) {
          groupedMedicines[type] = [];
        }
        groupedMedicines[type].push({
          ...med,
          medicineName: med.medicineName || med.name || "",
        });
      });

      const parseJSONSafe = (value) => {
        try {
          return JSON.parse(value);
        } catch (e) {
          return [];
        }
      };

      const parseNoteSafe = (noteValue) => {
        if (!noteValue) return "";
        try {
          const parsed = JSON.parse(noteValue);
          return typeof parsed === "string" ? parsed : noteValue;
        } catch {
          return noteValue;
        }
      };

      setMedicines(groupedMedicines);
      setFoods({
        foodToEat: Array.isArray(prescription.foodToEat)
          ? prescription.foodToEat
          : parseJSONSafe(prescription.foodToEat),
        foodToAvoid: Array.isArray(prescription.foodToAvoid)
          ? prescription.foodToAvoid
          : parseJSONSafe(prescription.foodToAvoid),
      });

      setComplaints(
        Array.isArray(prescription.presentingComplaints) &&
          prescription.presentingComplaints.length
          ? prescription.presentingComplaints
          : [{ ...defaultComplaint }]
      );
      setNextFollowup(prescription.nextFollowup || null);
      setRepeatTests(prescription.repeatTests || "");
      setNote(parseNoteSafe(prescription.note));
    } else {
      setMedicines({
        meds: [{ ...defaultMedicine }],
      });
      setFoods({ foodToEat: [""], foodToAvoid: [""] });
      setComplaints([{ ...defaultComplaint }]);
      setRootCauseFactors([{ term: "", detail: "" }]);
      setLifestylePlan([""]);
      setYogaAsanas([{ asana: "", duration: "" }]);
      setNextFollowup(null);
      setRepeatTests("");
      setNote("");
    }
  }, [prescription]);

  const updateMedicines = (type, updatedMedicines) => {
    setMedicines((prevState) => ({
      ...prevState,
      [type]: updatedMedicines,
    }));
  };

  const handleSubmit = () => {
    let mappedMedicines = [];
    Object.keys(medicines).forEach((key) => {
      medicines[key].forEach((med) => {
        mappedMedicines.push({
          name: med.medicineName || "",
          dosage: med.dosage || "",
          frequency: med.frequency || "",
          duration: med.duration || "",
          instruction: med.instruction || "",
          medicineTime: med.medicineTime || "",
          type: key,
        });
      });
    });

    const presData = {
      caseId: caseId,
      doctorId: selectedDoctorId,
      nextFollowup: nextFollowup,
      medicines: mappedMedicines,
      foodToEat: foods.foodToEat,
      foodToAvoid: foods.foodToAvoid,
      presentingComplaints: complaints.filter((c) => c.complaint),
      repeatTests: repeatTests,
      note: note,
      // The patient's summary is sent once the report PDF exists, so it can be
      // attached — see submitReport below.
      deferSummary: true,
    };

    // Queue the Ayurvedic prescription PDF alongside the case record. A failure
    // here must not lose the prescription the doctor just saved, so it is
    // reported and swallowed rather than blocking the close.
    const submitReport = (saved) => {
      const loggedInUser = getUserInfo() || {};
      const reportPayload = buildPrescriptionReportPayload({
        patient: patient || {},
        externalReport: externalReport || {},
        doctor: loggedInUser,
        complaints: presData.presentingComplaints,
        medicines: mappedMedicines,
        nextFollowup: nextFollowup,
        repeatTests: repeatTests,
        rootCauseFactors: rootCauseFactors,
        lifestylePlan: lifestylePlan,
        yogaAsanas: yogaAsanas,
        nutrition: { add: foods.foodToEat, avoid: foods.foodToAvoid },
      });
      // Lets the backend link the generated report to this prescription row, so
      // the list can re-sign a fresh PDF url from its report_id.
      reportPayload.prescription_id = saved?.id;
      return dispatch(submitPrescriptionReportRequest(reportPayload)).catch(
        (error) => {
          const reason =
            error?.response?.data?.error ||
            error?.response?.data?.message ||
            "Please try again";
          dispatch(
            showSnackbar(
              `Prescription saved, but the Ayurvedic report could not be generated. ${reason}`,
              "warning"
            )
          );
          return null;
        }
      );
    };

    if (prescription?.id) {
      dispatch(updatePrescriptionRequest(prescription.id, presData)).then(
        () => {
          setIsGoBackClicked(true);
          onClosePrescriptionForm();
        }
      );
    } else {
      dispatch(addPrescriptionRequest(presData))
        .then(submitReport)
        .then(() => {
          setIsGoBackClicked(true);
          onClosePrescriptionForm();
        });
    }
  };

  return (
    <Box component={Paper}>
      <ValidatorForm
        onSubmit={handleSubmit}
        style={{ padding: "24px", marginTop: "24px" }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box className={classes.cardHeader}>
              <Typography variant="h6">
                <Button
                  startIcon={<ArrowBackIosIcon />}
                  className={classes.backButton}
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    setIsGoBackClicked(true);
                    onClosePrescriptionForm();
                  }}
                >
                  Back
                </Button>
                {prescription ? "Edit Prescription" : "Add Prescription"}
              </Typography>
            </Box>
          </Grid>

          {/* Presenting Complaints */}
          <Grid item xs={12}>
            <SectionRows
              title="Presenting Complaints"
              addLabel="Add Complaint"
              fields={COMPLAINT_FIELDS}
              rows={complaints}
              updateRows={setComplaints}
            />
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* Medicines */}
          <Grid item xs={12}>
            {Object.keys(medicines).map((type) => (
              <Medicines
                key={type}
                medicines={
                  Array.isArray(medicines[type]) ? medicines[type] : []
                }
                type={type}
                allMedicines={allMedicines}
                addNewMedicine={() =>
                  setMedicines((prev) => ({
                    ...prev,
                    [type]: [...(prev[type] || []), { ...defaultMedicine }],
                  }))
                }
                updateMedicines={updateMedicines}
                removeMedicine={(index) =>
                  setMedicines((prev) => ({
                    ...prev,
                    [type]: prev[type].filter((_, i) => i !== index),
                  }))
                }
              />
            ))}
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* Nutrition */}
          <Grid item xs={12} className={classes.margiTop16}>
            <Typography variant="h6">Nutrition</Typography>
          </Grid>

          <Grid item xs={12}>
            <Foods
              type={FOOD_TYPES.FOOD_TO_EAT}
              foods={foods.foodToEat}
              addFood={() =>
                setFoods((prev) => ({
                  ...prev,
                  foodToEat: [...prev.foodToEat, ""],
                }))
              }
              updateFood={(updated) =>
                setFoods((prev) => ({
                  ...prev,
                  foodToEat: updated,
                }))
              }
              removeFood={(index) =>
                setFoods((prev) => ({
                  ...prev,
                  foodToEat: prev.foodToEat.filter((_, i) => i !== index),
                }))
              }
            />
          </Grid>

          <Grid item xs={12}>
            <Foods
              type={FOOD_TYPES.FOOD_TO_AVOID}
              foods={foods.foodToAvoid}
              addFood={() =>
                setFoods((prev) => ({
                  ...prev,
                  foodToAvoid: [...prev.foodToAvoid, ""],
                }))
              }
              updateFood={(updated) =>
                setFoods((prev) => ({
                  ...prev,
                  foodToAvoid: updated,
                }))
              }
              removeFood={(index) =>
                setFoods((prev) => ({
                  ...prev,
                  foodToAvoid: prev.foodToAvoid.filter((_, i) => i !== index),
                }))
              }
            />
          </Grid>

          {/* Root Cause (Nidana) */}
          <Grid item xs={12}>
            <SectionRows
              title="Root Cause (Nidana)"
              addLabel="Add Factor"
              fields={ROOT_CAUSE_FIELDS}
              rows={rootCauseFactors}
              updateRows={setRootCauseFactors}
            />
          </Grid>

          {/* Lifestyle Plan */}
          <Grid item xs={12}>
            <SectionRows
              title="Lifestyle Plan"
              addLabel="Add Recommendation"
              fields={LIFESTYLE_FIELDS}
              rows={lifestylePlan}
              updateRows={setLifestylePlan}
            />
          </Grid>

          {/* Yoga Asanas */}
          <Grid item xs={12}>
            <SectionRows
              title="Yoga Asanas"
              addLabel="Add Asana"
              fields={YOGA_FIELDS}
              rows={yogaAsanas}
              updateRows={setYogaAsanas}
            />
          </Grid>

          {/* Follow-up */}
          <Grid item xs={12} className={classes.margiTop16}>
            <Typography variant="h6">Follow-up Details</Typography>
          </Grid>

          {/* Doctor Selection */}
          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={doctors
                .filter((doc) => doc && (doc.firstName || doc.lastName))
                .sort((a, b) =>
                  (a.firstName || "").localeCompare(a.firstName || "")
                )
                .map((doctor) => ({
                  label: `${doctor.firstName || ""} ${
                    doctor.lastName || ""
                  }`.trim(),
                  value: doctor.userId,
                }))}
              value={
                doctors
                  .filter((doc) => doc && (doc.firstName || doc.lastName))
                  .map((doctor) => ({
                    label: `${doctor.firstName || ""} ${
                      doctor.lastName || ""
                    }`.trim(),
                    value: doctor.userId,
                  }))
                  .find((option) => option.value === selectedDoctorId) || null
              }
              onChange={(_, newValue) => {
                setSelectedDoctorId(newValue ? newValue.value : "");
                setDoctorInput(newValue ? newValue.label : "");
              }}
              getOptionLabel={(option) =>
                typeof option.label === "string" ? option.label : ""
              }
              getOptionSelected={(option, value) =>
                option.value === value.value
              }
              renderInput={(params) => (
                <TextValidator
                  {...params}
                  value={doctorInput}
                  onChange={(e) => setDoctorInput(e.target.value)}
                  label="Select Doctor for Follow-up*"
                  variant="outlined"
                  size="small"
                  fullWidth
                  validators={["requiredDoctor"]}
                  errorMessages={["Follow-up doctor is required"]}
                />
              )}
            />
          </Grid>

          {/* Next Follow-up Date */}
          <Grid item xs={12} sm={6}>
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
              <DatePickerValidator
                fullWidth
                label="Next Follow-up*"
                inputVariant="outlined"
                size="small"
                format="dd/MM/yyyy"
                minDate={new Date()}
                value={nextFollowup}
                onChange={setNextFollowup}
                validators={["required"]}
                errorMessages={["Next follow-up date is required"]}
              />
            </MuiPickersUtilsProvider>
          </Grid>

          {/* Repeat Tests */}
          <Grid item xs={12}>
            <TextValidator
              label="Repeat Tests"
              variant="outlined"
              fullWidth
              size="small"
              value={repeatTests}
              onChange={(e) => setRepeatTests(e.target.value)}
              validators={[]}
              errorMessages={[]}
              placeholder="E.g., HbA1c, Vitamin D & Fasting Insulin after 3 months"
            />
          </Grid>

          {/* Notes */}
          <Grid item xs={12}>
            <TextValidator
              label="Add Note"
              variant="outlined"
              fullWidth
              size="small"
              multiline
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              validators={[]}
              errorMessages={[]}
              placeholder="Write any additional notes here"
            />
          </Grid>

          {/* Submit */}
          <Grid item xs={12} className={classes.margiTop16}>
            <Button type="submit" variant="contained" color="primary" fullWidth>
              {prescription ? "Update Prescription" : "Submit Prescription"}
            </Button>
          </Grid>
        </Grid>
      </ValidatorForm>
    </Box>
  );
};

export default PrescriptionForm;
